/**
 * Shared derivation helpers for the Today SOC chart.
 * Keep these functions pure so they’re easy to test and reuse.
 */
import type { BatteryActivityType } from '../../../utils/socChartUtils';
export type ShadingPoint = {
  x: string;
  y: number;
  data: { target_datetime_utc: number };
};

type ChartPoint = {
  x: string;
  y?: number;
  _ts?: number;
};

export const buildShadingPoints = (behaviorChartData: ChartPoint[] | null | undefined): ShadingPoint[] => {
  if (!Array.isArray(behaviorChartData) || behaviorChartData.length === 0) return [];

  return behaviorChartData
    .map((point): ShadingPoint | null => {
      const ts = point?._ts;
      if (typeof ts !== 'number' || !Number.isFinite(ts)) return null;

      return {
        x: point.x,
        y: point.y ?? 0,
        data: { target_datetime_utc: ts },
      };
    })
    .filter((p): p is ShadingPoint => p !== null);
};

type Segment = {
  activityType: BatteryActivityType;
  data: Array<{ x: string; y: number }>;
};

export type NowToFutureLink = {
  activityType: BatteryActivityType;
  data: Array<{ x: string; y: number }>;
};

export const buildNowToFutureLink = ({
  intersectionX,
  intersectionY,
  futureChartSegments,
}: {
  intersectionX: string | null | undefined;
  intersectionY: number;
  futureChartSegments: Segment[] | null | undefined;
}): NowToFutureLink | null => {
  if (!intersectionX || !Number.isFinite(intersectionY)) return null;
  if (!Array.isArray(futureChartSegments) || futureChartSegments.length === 0) return null;

  const firstSegment = futureChartSegments[0];
  const firstFuturePoint = firstSegment?.data?.[0];

  if (!firstFuturePoint?.x || !Number.isFinite(firstFuturePoint?.y)) return null;
  if (firstFuturePoint.x === intersectionX) return null;

  return {
    activityType: firstSegment.activityType,
    data: [
      { x: intersectionX, y: intersectionY },
      { x: firstFuturePoint.x, y: firstFuturePoint.y },
    ],
  };
};
